import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tag, Trash2 } from "lucide-react";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", percent: "", description: "", kind: "percent", daily_rate: "50", expires_at: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim() || (form.kind === "percent" && !form.percent) || (form.kind === "flat_rate" && !form.daily_rate)) return toast.error(form.kind === "percent" ? "Code and discount % are required" : form.kind === "flat_rate" ? "Code and daily rate are required" : "Code is required");
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim(),
        percent: form.kind === "percent" ? Number(form.percent) : 0,
        kind: form.kind,
        daily_rate: form.kind === "flat_rate" ? Number(form.daily_rate) : null,
        expires_at: form.expires_at || null,
        description: form.description,
        active: true,
      });
      toast.success(form.kind === "waive_deposit" ? "Deposit-waiver code created" : form.kind === "flat_rate" ? "Buddy pass code created" : "Discount code created");
      setForm({ code: "", percent: "", description: "", kind: "percent", daily_rate: "50", expires_at: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/coupons/${id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete discount code ${c.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Discount code deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-coupons-tab">
      <AdminHeader testid="admin-header-coupons" eyebrow="Marketing" title="Discount Codes" description="Create and manage promo codes for checkout." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6 h-fit">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <Tag size={14} className="text-[#C71E2E]" /> New Discount Code
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Code</Label>
            <Input data-testid="coupon-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ISLAND20" className="input-dark mt-1.5 uppercase" />
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Type</Label>
            <select data-testid="coupon-form-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input-dark mt-1.5 w-full h-10 rounded-md border border-[#E5DED5] px-3 text-sm bg-transparent font-ui">
              <option value="percent">Percent off rental</option>
              <option value="waive_deposit">Remove security deposit only</option>
              <option value="flat_rate">Buddy pass — flat daily rate + no deposit</option>
            </select>
          </div>
          {form.kind === "percent" && (
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Discount (%)</Label>
              <Input data-testid="coupon-form-percent" type="number" min={1} max={100} value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} placeholder="15" className="input-dark mt-1.5" />
            </div>
          )}
          {form.kind === "flat_rate" && (
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Flat daily rate ($/day)</Label>
              <Input data-testid="coupon-form-daily-rate" type="number" min={1} value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} placeholder="50" className="input-dark mt-1.5" />
              <p className="text-[11px] text-muted-foreground font-ui mt-1.5">Rental bills at this flat rate per day; security deposit is removed; tax &amp; processing fee still apply. Weekly/loyalty discounts don't stack.</p>
            </div>
          )}
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Description (optional)</Label>
            <Input data-testid="coupon-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Spring promotion" className="input-dark mt-1.5" />
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Expiry date (optional)</Label>
            <Input data-testid="coupon-form-expiry" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="input-dark mt-1.5" />
            <p className="text-[11px] text-muted-foreground font-ui mt-1.5">Code stops working at the end of this day. Leave empty for no expiry.</p>
          </div>
          <Button data-testid="coupon-submit-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
            {saving ? "Creating…" : "Create Discount Code"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-2 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto h-fit">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={5} data-testid="coupons-empty" className="text-muted-foreground font-ui py-10 text-center">No discount codes yet.</TableCell>
              </TableRow>
            )}
            {coupons.map((c) => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.id}`} className={ROW}>
                <TableCell className="text-[#C71E2E] font-ui font-semibold">{c.code}</TableCell>
                <TableCell className="font-ui">{c.kind === "waive_deposit" ? <span data-testid={`coupon-kind-waive-${c.id}`} className="text-emerald-700">Deposit removed</span> : c.kind === "flat_rate" ? <span data-testid={`coupon-kind-flat-${c.id}`} className="text-emerald-700">${c.daily_rate}/day + no deposit</span> : `${c.percent}%`}</TableCell>
                <TableCell className="text-muted-foreground">{c.description || "—"}{c.expires_at && <span className={`block text-[10px] mt-0.5 ${c.expires_at < new Date().toISOString() ? "text-red-600 font-semibold" : ""}`}>{c.expires_at < new Date().toISOString() ? "Expired" : "Expires"} {c.expires_at.slice(0, 10)}</span>}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${c.active ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-muted-foreground border-[#E5DED5] bg-[#FAF6F0]"}`}>
                    {c.active ? "Active" : "Disabled"}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button data-testid={`coupon-toggle-${c.id}`} size="sm" variant="outline" onClick={() => toggle(c.id)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui mr-2">
                    {c.active ? "Disable" : "Enable"}
                  </Button>
                  <button data-testid={`coupon-delete-${c.id}`} onClick={() => remove(c)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete discount code">
                    <Trash2 size={15} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </div>
    </div>
  );
}
